import React, { useMemo } from 'react';
import { Switch, Icon, colors, withDataId, FieldGroupItem } from '@dexma/ui-components';
import {
    StyledFakeSelector,
    StyledPeriodTypeFieldGroup,
    StyledRadioButtonWrapper,
    StyledRadioFieldGroup,
    StyledSelectablePeriodTemplate,
    StyledSwitchGroup,
} from 'adapter/in/common/components/PeriodSelector/style';

import { areRollingStatesEqual } from 'adapter/in/common/components/PeriodSelector/periodSelectorUtils';
import { PeriodSelectorTexts, PeriodType, SelectablePeriodTemplateState } from 'adapter/in/common/components/PeriodSelector';
import gtm from 'adapter/in/common/config/gtm';
import { PeriodFormState } from 'adapter/in/common/form/types';
import { ENTITY_TYPE } from 'adapter/in/common/config/entityTypes';
import i18n from 'asset/translation';

type PeriodTypeFieldGroupProps = {
    title: string;
    texts: PeriodSelectorTexts;
    handleChange: (id: PeriodType) => void;
    selectedPeriodType: PeriodType;
    hasContext: boolean;
};
export const PeriodTypeFieldGroup = (props: PeriodTypeFieldGroupProps) => {
    const { title, texts, handleChange, selectedPeriodType, hasContext } = props;

    const correctText = useMemo(
        () => i18n.t(selectedPeriodType === PeriodType.FIXED ? texts.periodTypeFixedText : texts.periodTypeRollingText),
        [selectedPeriodType]
    );

    return (
        <StyledPeriodTypeFieldGroup>
            <span>{title}</span>
            <StyledRadioFieldGroup
                followContext={hasContext}
                onChange={(item: FieldGroupItem) => {
                    handleChange(item.id as PeriodType);
                }}
                selectedValues={selectedPeriodType}
                dataId={gtm.periodSelector.rollingAndFixed}
                values={[
                    ...(hasContext
                        ? [{ value: PeriodType.CONTEXT, label: i18n.t(texts.periodTypeContextLabel), id: PeriodType.CONTEXT }]
                        : []),
                    { value: PeriodType.ROLLING, label: i18n.t(texts.periodTypeRollingLabel), id: PeriodType.ROLLING },
                    { value: PeriodType.FIXED, label: i18n.t(texts.periodTypeFixedLabel), id: PeriodType.FIXED },
                ]}
            />
            <span className='period-type-description'>{correctText}</span>
        </StyledPeriodTypeFieldGroup>
    );
};

export type SelectablePeriodTemplatesProps = {
    periodValues?: SelectablePeriodTemplateState[];
    handleChange: (state: PeriodFormState) => void;
    formState: PeriodFormState;
    primaryColor: string;
    areDisabled: boolean;
};
export const SelectablePeriodTemplates = (props: SelectablePeriodTemplatesProps) => {
    const { periodValues, handleChange, formState, primaryColor, areDisabled: isDisabled } = props;

    const handleRadioChange = (radioState: PeriodFormState) => {
        handleChange(radioState);
    };

    return (
        <StyledSelectablePeriodTemplate>
            {periodValues?.map((periodTemplate: SelectablePeriodTemplateState) => (
                <PeriodRadioButton
                    color={primaryColor}
                    isDisabled={isDisabled}
                    key={periodTemplate.id}
                    label={periodTemplate.label}
                    radioButtonState={periodTemplate.state}
                    isChecked={areRollingStatesEqual(periodTemplate.state, formState)}
                    handleRadioChange={handleRadioChange}
                    dataId={gtm.periodSelector.template}
                    data-entity-id={periodTemplate.id}
                    data-entity-type={ENTITY_TYPE.PERIOD_TEMPLATE}
                />
            ))}
        </StyledSelectablePeriodTemplate>
    );
};

type PeriodRadioButtonProps = {
    label: string;
    isChecked: boolean;
    handleRadioChange: (state: PeriodFormState) => void;
    radioButtonState: PeriodFormState;
    color: string;
    isDisabled: boolean;
    dataId: string;
    'data-entity-id'?: string;
    'data-entity-type'?: string;
};
const PeriodRadioButtonNoId = (props: PeriodRadioButtonProps) => {
    const { label, isChecked, handleRadioChange, radioButtonState, color, isDisabled, dataId,
        'data-entity-id': entityId, 'data-entity-type': entityType } = props;

    return (
        <StyledRadioButtonWrapper color={color} checked={isChecked} $isDisabled={isDisabled} tabIndex={0}
            data-entity-id={entityId} data-entity-type={entityType}>
            <input
                onClick={
                    !isDisabled
                        ? () => {
                              handleRadioChange(radioButtonState);
                          }
                        : undefined
                }
                type='radio'
                name='periodValue'
                checked={isChecked}
                disabled={isDisabled}
                readOnly
                data-id={dataId}
            />
            <span>{label}</span>
        </StyledRadioButtonWrapper>
    );
};

export const PeriodRadioButton = withDataId(PeriodRadioButtonNoId, 'period-radio-button');

type SwitchGroupProps = {
    title: string;
    isChecked: boolean;
    onChange: () => void;
    $isDisabled?: boolean;
    id?: string;
};
export const SwitchGroup = (props: SwitchGroupProps) => {
    const { title, isChecked, onChange, $isDisabled, id } = props;

    return (
        <StyledSwitchGroup data-entity-id={id} data-entity-type={ENTITY_TYPE.PERIOD_SWITCH}>
            <Switch
                dataId={gtm.periodSelector.switch}
                disabled={$isDisabled}
                size='small'
                checked={isChecked}
                onChange={() => onChange()}
            />
            <span>{title}</span>
        </StyledSwitchGroup>
    );
};

type FakeSelectorProps = {
    dataId: string;
    color?: string;
    disabled?: boolean;
    label?: string;
    isUIOpen?: boolean;
};
const FakeSelectorNoId = (props: FakeSelectorProps) => (
    <StyledFakeSelector data-id={props.dataId} color={props.color} tabIndex={0} $disabled={props.disabled}>
        <span>{props.label}</span>
        <Icon
            name={props.isUIOpen ? 'chevron_up' : 'chevron_down'}
            size='small'
            color={colors.gray}
            ariaLabel={i18n.t(
                props.isUIOpen ? 'common.periodSelector.chevronUpIconAriaLabel' : 'common.periodSelector.chevronDownIconAriaLabel'
            )}
        />
    </StyledFakeSelector>
);

export const FakeSelector = withDataId(FakeSelectorNoId, 'fake-selector');
